import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IStatus } from "shared/types";
import { ConfirmModal } from "shared/components";
import { copy } from "shared/assets";

import { dataMetaSelector, sendBandInvitation } from "Bands/state";
import { BAND_ACTIONS, IBandInvitation } from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { User } from "shared/assets/icons";
import {
  InvitationActionSourceType,
  useBandContext,
  useBandInvitationContext,
} from "Bands/contexts";

const sendInvitationCopy = copy.members.sendInvitation;

interface SendBandInvitationModalProps {
  invitation: IBandInvitation;
  handleClose: () => void;
}

export const SendBandInvitationModal: React.FC<SendBandInvitationModalProps> = ({
  invitation,
  handleClose,
}) => {
  const dispatch = useDispatch();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.sendBandInvitation));

  const { bandId, band } = useBandContext();
  const { fetchSentInvitations, fetchPendingInvitations, invitationActionSource } =
    useBandInvitationContext();

  useClearActionMetaOnUnMount(BAND_ACTIONS.sendBandInvitation);

  useEffect(() => {
    if (!bandId) return;

    if (apiMeta?.status === IAPIState.succeeded) {
      // if action triggered from pending table
      // refetech pending and sent invitations
      // if action triggered from sent table, don't refetch (invitations are not updated, new mail is triggered)
      if (invitationActionSource === InvitationActionSourceType.pending) {
        fetchPendingInvitations();
        fetchSentInvitations();
      }

      handleClose();
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: sendInvitationCopy.messages.success,
        })
      );
    }
  }, [
    dispatch,
    apiMeta,
    handleClose,
    bandId,
    fetchSentInvitations,
    fetchPendingInvitations,
    invitationActionSource,
  ]);

  const handleConfirm = () => {
    if (invitation) {
      dispatch(sendBandInvitation(invitation.id));
    }
  };

  const message = useMemo(
    () =>
      sendInvitationCopy.messages.modal
        .replace("%email", invitation.email)
        .replace("%band", band?.title!)
        .toString(),
    [invitation.email, band?.title]
  );

  return (
    <ConfirmModal
      title={sendInvitationCopy.title}
      icon={User}
      message={message}
      confirmButtonCopy={sendInvitationCopy.buttons.primary}
      handleConfirm={handleConfirm}
      handleClose={handleClose}
      apiMeta={apiMeta}
    />
  );
};
